import {
  Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Typography,
} from '@mui/material';
import { CloudUpload, ExpandLess, ExpandMore, WarningAmber } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useRef, useState } from 'react';
import { parseRmcplotsFile, detectStageFromFilename, isSupportedRmcplotsFile, RMCPLOTS_UPLOAD_ACCEPT } from './rmcplotsParser';
import type { ParsedStageData } from '../../../../types/directShearReading';

interface DirectShearUploadProps {
  expanded: boolean;
  onToggle: () => void;
  onUpload: (data: ParsedStageData) => Promise<void>;
  onUploadComplete: () => Promise<void>;
  stageCounts: Record<number, number>;
}

export const DirectShearUpload = ({ expanded, onToggle, onUpload, onUploadComplete, stageCounts }: DirectShearUploadProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  // Confirm dialog for overwriting existing stages
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    conflictStages: number[];
    files: File[];
  }>({ open: false, conflictStages: [], files: [] });

  const processAllFiles = useCallback(async (files: File[]) => {
    setProcessing(true);
    setError('');
    const summaries: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(`[${i + 1}/${files.length}] Parsing ${file.name}...`);
      try {
        const result = await parseRmcplotsFile(file);
        if (result.stageNumber === null) {
          setError(`Could not detect stage from "${file.name}". Expected _stage1 through _stage5.`);
          continue;
        }
        if (result.readings.length === 0) {
          setError(`No data in "${file.name}" (all rows zero).`);
          continue;
        }
        const skippedParts: string[] = [];
        if (result.leadingRowsSkipped > 0) skippedParts.push(`${result.leadingRowsSkipped} leading`);
        if (result.trailingRetractionStripped > 0) skippedParts.push(`${result.trailingRetractionStripped} trailing`);
        const seatingNote = result.seatingRowsKept > 0 ? `, ${result.seatingRowsKept} seating rows kept for the stress path` : '';
        const skipNote = (skippedParts.length > 0 ? `, skipped ${skippedParts.join(' + ')}` : '') + seatingNote;

        setProgress(`[${i + 1}/${files.length}] Uploading Stage ${result.stageNumber}: ${result.readings.length} rows...`);
        await onUpload({ stageNumber: result.stageNumber, readings: result.readings });
        summaries.push(`Stage ${result.stageNumber}: ${result.readings.length} rows${skipNote}`);
      } catch (err) {
        setError(`Failed on "${file.name}": ${err instanceof Error ? err.message : 'Unknown error'}`);
        break;
      }
    }
    setProgress('Refreshing data...');
    await onUploadComplete();
    setProcessing(false);
    setProgress(summaries.length > 0 ? `Done — ${summaries.join(' | ')}` : '');
  }, [onUpload, onUploadComplete]);

  const handleFiles = useCallback(async (files: FileList) => {
    setError('');
    // Validate extensions and detect stages
    const stageMap = new Map<number, string>();
    for (const file of Array.from(files)) {
      if (!isSupportedRmcplotsFile(file.name)) {
        setError(`"${file.name}" is not a .rmcplots or Excel (.xlsx) file.`);
        return;
      }
      const stage = detectStageFromFilename(file.name);
      if (stage !== null && stageMap.has(stage)) {
        setError(`Two files map to Stage ${stage}. Upload one file per stage.`);
        return;
      }
      if (stage !== null) stageMap.set(stage, file.name);
    }

    // Check which stages already have data
    const conflictStages = Array.from(stageMap.keys()).filter((s) => stageCounts[s] > 0);
    const fileArray = Array.from(files);

    if (conflictStages.length > 0) {
      // Show single confirmation dialog listing all conflicting stages
      setConfirmDialog({ open: true, conflictStages, files: fileArray });
    } else {
      // No conflicts — proceed directly
      await processAllFiles(fileArray);
    }
  }, [stageCounts, processAllFiles]);

  const handleConfirm = useCallback(async () => {
    const { files } = confirmDialog;
    setConfirmDialog({ open: false, conflictStages: [], files: [] });
    await processAllFiles(files);
  }, [confirmDialog, processAllFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box onClick={onToggle}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1,
          cursor: 'pointer', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>Bulk Data Upload (All Stages)</Typography>
          {[1, 2, 3, 4, 5].map((s) => (
            <Chip key={s} label={`S${s}: ${stageCounts[s] || 0}`} size="small"
              sx={{ height: 20, fontSize: '0.65rem',
                backgroundColor: stageCounts[s] > 0 ? alpha(theme.palette.success.main, 0.1) : alpha('#000', 0.04),
                color: stageCounts[s] > 0 ? 'success.dark' : 'text.disabled' }} />
          ))}
        </Box>
        <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Box onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}
            onClick={() => !processing && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: 2, py: 3, textAlign: 'center',
              cursor: processing ? 'wait' : 'pointer',
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              transition: 'all 0.2s',
              '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.05) },
            }}>
            <CloudUpload sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.4), mb: 0.5 }} />
            <Typography variant="body2" color={processing ? 'primary.main' : progress ? 'success.dark' : 'text.secondary'}
              sx={{ fontWeight: processing || progress ? 600 : 400 }}>
              {processing ? (progress || 'Processing...') : progress || 'Drop .rmcplots or Excel (.xlsx) files here (1–5 files, one per stage)'}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Files auto-assign to stages by filename (_stage1, _stage2, ...) — same columns and rules for both formats
            </Typography>
            <input ref={fileInputRef} type="file" accept={RMCPLOTS_UPLOAD_ACCEPT} multiple hidden
              onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }} />
          </Box>
          {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
        </Box>
      </Collapse>

      {/* Single confirmation dialog for all conflicting stages */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, conflictStages: [], files: [] })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, pb: 0 }}>
          <WarningAmber sx={{ color: 'warning.main' }} /> Replace Existing Data?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {confirmDialog.conflictStages.length === 1
              ? `Stage ${confirmDialog.conflictStages[0]} already has ${stageCounts[confirmDialog.conflictStages[0]] || 0} rows.`
              : `Stages ${confirmDialog.conflictStages.join(', ')} already have data.`}
            {' '}Uploading will replace existing readings with data from the new files.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog({ open: false, conflictStages: [], files: [] })}
            sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirm}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Replace & Upload</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
